import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, UserCheck, Plus } from 'lucide-react';

const ROLE_LABELS = { TEAM_LEADER: 'Leader', PRIMARY_SUPPORT_WORKER: 'Primary', SECONDARY_SUPPORT_WORKER: 'Secondary', COORDINATOR: 'Coordinator', CLINICAL_STAFF: 'Clinical', ADMIN_SUPPORT: 'Admin', CASUAL_POOL: 'Casual' };

export default function HouseTeamsTab({ houseId }) {
  const { isAdmin } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/teams/by-house/${houseId}`)
      .then(r => setTeams(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [houseId]);

  if (loading) return <p className="text-muted-foreground py-4">Loading teams...</p>;

  return (
    <div className="space-y-4" data-testid="house-teams-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Linked Teams</h3>
        {isAdmin && (
          <Button size="sm" onClick={() => navigate('/teams/new')} data-testid="create-house-team-btn">
            <Plus className="w-4 h-4 mr-2" />Create Team
          </Button>
        )}
      </div>

      {teams.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No teams linked to this house</p>
            <p className="text-sm mt-1">Create a SIL House Team and link it to this property</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {teams.map(t => (
            <Card key={t.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/teams/${t.id}`)} data-testid={`house-team-${t.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    <span className="font-medium">{t.team_name}</span>
                    <Badge variant="outline" className="text-xs">{t.team_type?.replace(/_/g, ' ')}</Badge>
                  </div>
                  <Badge>{t.status}</Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span><UserCheck className="w-3 h-3 inline mr-0.5" />{t.member_count || 0} members</span>
                  {t.leader_name && <span>Leader: {t.leader_name}</span>}
                  {t.branch_id && <span>{t.branch_id}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
